import {
  AUTOMATED_TASK_TYPES,
  MANUAL_TASK_TYPES,
  TASK_TYPE_LABELS,
  TASK_TYPES,
  type TaskType,
  type TaskTypeCounts,
} from '@crm/contracts';
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from '../ui/dropdown-menu.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { TaskFilterChip } from './task-filter-chip.tsx';

export type TaskKind = 'manual' | 'automated' | 'all';

const LABELS: Record<TaskKind, string> = {
  manual: 'Manual Tasks',
  automated: 'Automated Tasks',
  all: 'All Tasks',
};

/** The API's `?automated=` for a kind: manual false, automated true, all absent. */
export const automatedFor = (kind: TaskKind) => (kind === 'all' ? undefined : kind === 'automated');

/** The types a task of this kind can have, so a type filter never offers an empty result. */
export const typesFor = (kind: TaskKind): readonly TaskType[] =>
  kind === 'manual' ? MANUAL_TASK_TYPES : kind === 'automated' ? AUTOMATED_TASK_TYPES : TASK_TYPES;

/** Starts on Manual everywhere: automated tasks are the system's to-do, shown when asked for. */
export function TaskKindSelect({
  value,
  onChange,
}: {
  value: TaskKind;
  onChange: (kind: TaskKind) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as TaskKind)}>
      <SelectTrigger className="h-8 w-44" aria-label="Task kind" data-testid="filter-kind">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(Object.keys(LABELS) as TaskKind[]).map((k) => (
          <SelectItem key={k} value={k}>
            {LABELS[k]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Manual, automated or all tasks; Manual is the default, so it only reads as a filter otherwise. */
export function TaskKindChip({
  kind,
  onChange,
}: {
  kind: TaskKind;
  onChange: (kind: TaskKind) => void;
}) {
  return (
    <TaskFilterChip
      name="Kind"
      label={LABELS[kind]}
      active={kind !== 'manual'}
      onClear={() => onChange('manual')}
      testId="filter-kind"
    >
      <DropdownMenuRadioGroup value={kind} onValueChange={(v) => onChange(v as TaskKind)}>
        {(Object.keys(LABELS) as TaskKind[]).map((k) => (
          <DropdownMenuRadioItem key={k} value={k}>
            {LABELS[k]}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </TaskFilterChip>
  );
}

/**
 * The task types the chosen kind can have, each with how many tasks it would list under the
 * other filters (`counts`, from the API; left out while they load or when they failed).
 */
export function TaskTypeChip({
  kind,
  type,
  onChange,
  counts,
}: {
  kind: TaskKind;
  type: TaskType | undefined;
  onChange: (type: TaskType | undefined) => void;
  counts?: TaskTypeCounts | undefined;
}) {
  return (
    <TaskFilterChip
      name="Type"
      label={type ? TASK_TYPE_LABELS[type] : 'Type'}
      active={!!type}
      onClear={() => onChange(undefined)}
      testId="filter-type"
    >
      <DropdownMenuRadioGroup value={type ?? ''} onValueChange={(v) => onChange(v as TaskType)}>
        {typesFor(kind).map((t) => (
          <DropdownMenuRadioItem key={t} value={t}>
            <span className="flex-1">{TASK_TYPE_LABELS[t]}</span>
            {counts && (
              <span className="text-muted-foreground pl-4 tabular-nums" data-testid="type-count">
                {counts.find((c) => c.type === t)?.count ?? 0}
              </span>
            )}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </TaskFilterChip>
  );
}
